import type { FilterCategory, SortConfig, TextOption } from '@/core/views/viewConfig'

export function searchItems<T>(
  list: readonly T[],
  query: string,
  fields: (item: T) => (string | null | undefined)[],
): T[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...list]
  return list.filter((item) => fields(item).some((f) => (f ?? '').toLowerCase().includes(q)))
}

export type SortState = { key: string; dir: 'asc' | 'desc' } | null

export function sortItems<T>(list: readonly T[], sort: SortState, keys: SortConfig<T>[]): T[] {
  const config = sort ? keys.find((k) => k.key === sort.key) : undefined
  if (!sort || !config) return [...list]
  const sign = sort.dir === 'desc' ? -1 : 1
  const cmp = (a: T, b: T): number => {
    const av = config.accessor(a)
    const bv = config.accessor(b)
    if (typeof av === 'number' && typeof bv === 'number') return av - bv
    return String(av).localeCompare(String(bv), 'de', { sensitivity: 'base' })
  }
  return [...list].sort((a, b) => sign * cmp(a, b))
}

export function normalizeText(value: string): string {
  return value.trim().toLocaleLowerCase('de')
}

export function dedupeTextOptions(values: Iterable<string>): TextOption[] {
  const order: string[] = []
  const countsByKey = new Map<string, Map<string, number>>()
  for (const raw of values) {
    const label = raw.trim()
    if (!label) continue
    const key = normalizeText(label)
    let counts = countsByKey.get(key)
    if (!counts) {
      counts = new Map()
      countsByKey.set(key, counts)
      order.push(key)
    }
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  return order.map((key) => {
    const counts = countsByKey.get(key)!
    let bestLabel = ''
    let bestCount = -1
    for (const [label, count] of counts) {
      if (count > bestCount) {
        bestLabel = label
        bestCount = count
      }
    }
    return { key, label: bestLabel }
  })
}

function isExcluded(excluded: string[], value: string): boolean {
  return excluded.length > 0 && excluded.includes(normalizeText(value))
}

export function isCategoryFilterActive(excludedByCategory: Record<string, string[]>): boolean {
  return Object.values(excludedByCategory).some((excluded) => excluded.length > 0)
}

export function filterByCategories<T>(
  list: readonly T[],
  categories: FilterCategory<T>[],
  excludedByCategory: Record<string, string[]>,
): T[] {
  if (!isCategoryFilterActive(excludedByCategory)) return [...list]
  return list.filter((item) =>
    categories.every((cat) => {
      const value = cat.accessor(item)
      const values = typeof value === 'string' ? [value] : value
      return values.some((entry) => !isExcluded(excludedByCategory[cat.id] ?? [], entry))
    }),
  )
}
